import { AttestationType } from '#app/server/db.server.ts'
import { Button } from '../buttons'
import InternalLink from '../common/internalLink'
import { ArrowRightIcon, MoreIcon } from '../icons'
import { TypographyHeading, TypographyParagraph } from '../typography'
import { useState } from 'react'
import IconButton from '../buttons/iconButton'
import HighlightedAttestationCard, { HighlightedAttestationCardSkeleton } from './highlightedAttestationCard'
import { User } from '#types/loreTypes.tsx'
import { Counter } from '#app/components/badgesAndTags'

export default function HighlightedAttestations({
	highlightedAttestations,
	user,
	loading = false,
	onRemoveHighlight,
}: {
	highlightedAttestations: AttestationType[]
	user: User
	loading?: boolean
	onRemoveHighlight?: (attestationIPFSCID: string, userId: string) => Promise<void>
}) {
	const [showAll, setShowAll] = useState(true)

	const handleRemoveHighlight = async (attestationIPFSCID: string) => {
		if (onRemoveHighlight) {
			await onRemoveHighlight(attestationIPFSCID, user.id);
		}
	};
	
	// If no highlighted attestations and not loading, don't render the component
	if (highlightedAttestations.length === 0 && !loading) {
		return null
	}
	
	// Limit the number of attestations to display unless showAll is true
	const displayedAttestations = showAll 
		? highlightedAttestations 
		: highlightedAttestations.slice(0, 6)

	return (
		<div className="my-24 flex w-full flex-col gap-y-4 px-0 lg:px-12">
			<div className="flex flex-1 items-center gap-x-4">
				<TypographyHeading
					size="medium"
					isBold={true}
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					Highlighted moments
				</TypographyHeading>
				{highlightedAttestations.length > 0 && (
					<Counter size="small" children={highlightedAttestations.length} />
				)}
			</div>

			<TypographyParagraph
				size="medium"
				additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
			>
				A curated selection of attestations highlighting significant moments about the art and its creator.
			</TypographyParagraph>
			
			<div className="pt-6 columns-1 gap-4 sm:columns-2">
				{!loading ? (
					displayedAttestations.length > 0 ? (
						displayedAttestations.map((attestation, index) => (
							<div key={index} className="mb-4 break-inside-avoid transition-all duration-300 ease-in-out">
								<HighlightedAttestationCard 
									attestation={attestation} 
									onRemoveHighlight={handleRemoveHighlight}
									profileUser={user}
								/>
							</div>
						))
					) : (
						<div className="col-span-3 py-8 text-center">
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
							>
								No highlighted work available.
							</TypographyParagraph>
						</div>
					)
				) : (
					Array.from({ length: 3 }).map((_, index) => (
						<div key={index} className="mb-4 w-full break-inside-avoid">
							<HighlightedAttestationCardSkeleton key={index} />
						</div>
					))
				)}
			</div>
		</div>
	)
} 